import {
  type ReactElement,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { DetectionPill } from "@/components/terminal/detection-pill"
import { SortHeader, StaticHeader } from "@/components/terminal/sort-header"
import { TerminalOverflowTooltip } from "@/components/terminal/terminal-overflow-tooltip"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { DetectionClass } from "@/constants/terminal/detection-class"
import { cn } from "@/lib/utils"

// One reading table for every view (Reassessments, the Key Risk and report
// tables, Request History, the Assessment legend). A table is its columns and
// rows; each column declares its role, and the role sets its width and
// wrapping, so no view picks its own. This owns the wrapper (it scrolls with
// the view, never sideways), the column row pinned under the view's header
// band, the sort, the row skin, the row height and the empty state.
//
// Every column but the grow column hugs its content, up to an optional cap
// past which it truncates to the shared tooltip:
// - identity: a company, its score square and cyan name.
// - text: a short value.
// - numeric: tabular figures.
// - date: the caption role, tabular.
// - list: governed tokens: the first one and "+N", with every token in the
//   shared tooltip, so the column hugs its widest single token plus the
//   affordance.
// - grow: the one long-text column (what happened, a definition). It takes
//   the remaining width and sits last; it clamps at two lines (or shows every
//   line) instead of truncating, and its rows grow for it.
// Everything aligns left, words and figures alike, and a head sits on its
// column's left edge (Dhanur, 26 October 2026). Columns are left-packed: one
// gap token separates them, and a table without a grow column never spreads
// to fill its well; the empty width stays on the right, while the row skin
// (tint, cyan edge, rules) still spans the well. One row inset holds the
// first cell off the row's edge and reserves the live-row edge's lane.

// One sort grammar for every table, the company list's (SortHeader): a head
// click sorts by that column, most first (the latest date, the most
// incidents), except a score, which sorts lowest, so worst, first; a second
// click flips it.
type SortValue<Row> = (row: Row) => string | number | null

// A reading table's column row is framed the same pinned or not: one rule
// below it at the table's inset, drawn on the cells so it travels with a
// pinned row, and nothing above it (Dhanur, 26 October 2026).
const TABLE_HEAD_FRAME_CLASS = "table-head-frame"

// The column row of a reading table inside a ViewFrame: it pins directly
// beneath the view's header band, at the height the band measures itself.
// Over a table, the band draws no hairline of its own.
export const PINNED_TABLE_HEAD_CLASS = `${TABLE_HEAD_FRAME_CLASS} bg-background sticky top-[var(--view-header-height,0px)] z-10`

// Horizontal padding is set per position (edgeClassName), never here, so no
// two padding classes ever compete for a cell.
export const HEAD_CLASS = "h-auto pt-0 pb-2 align-bottom"
const CELL_CLASS =
  "h-(--home-table-row-min-height) py-(--home-table-row-padding-y) align-middle leading-normal font-normal"

// A cell's horizontal padding by its place in the row: the row inset at the
// row's two edges (it reserves the live-row edge's lane and a clear gap, so
// content never sits under the edge), the column gap between columns.
export const edgeClassName = (index: number, lastIndex: number) =>
  cn(
    index === 0 ? "pl-(--terminal-row-inset)" : "pl-0",
    index === lastIndex
      ? "pr-(--terminal-row-inset)"
      : "pr-(--terminal-table-column-gap)"
  )

// Unsorted until a head is clicked, unless the table opens on `initialSort`.
// Ties keep the rows' given order.
export const useTableSort = <Row, Key extends string>(
  rows: Row[],
  sortValueByKey: Partial<Record<Key, SortValue<Row>>>,
  initialSort: { key: Key; isDescending: boolean } | null = null
) => {
  const [sort, setSort] = useState(initialSort)
  const sortedRows = useMemo(() => {
    const sortValue = sort && sortValueByKey[sort.key]
    if (!sort || !sortValue) return rows
    return [...rows].sort((a, b) => {
      const av = sortValue(a)
      const bv = sortValue(b)
      // A missing value sorts last in either direction.
      if (av === null || bv === null)
        return Number(av === null) - Number(bv === null)
      const comparison =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv))
      return sort.isDescending ? -comparison : comparison
    })
  }, [rows, sort, sortValueByKey])
  // A column's first click sorts descending unless it reads worst-first
  // ascending (a score).
  const toggleSort = (key: Key, isAscendingFirst = false) =>
    setSort((prev) => ({
      key,
      isDescending: prev?.key === key ? !prev.isDescending : !isAscendingFirst,
    }))
  const ariaSort = (key: Key) => {
    if (sort?.key !== key) return "none"
    return sort.isDescending ? "descending" : "ascending"
  }
  return { sort, sortedRows, toggleSort, ariaSort }
}

// The absence dash: a missing value in any table cell (DESIGN.md, Tables).
export const AbsenceDash = () => (
  <span className="type-data text-text-disabled">—</span>
)

type ReadingColumnRole = "identity" | "text" | "numeric" | "date" | "list"

type ColumnBase<Row> = {
  key: string
  label: string
  // Read by screen readers only (the score square's column), unless the
  // column sorts.
  isLabelHidden?: boolean
  // The score square's column: its head is the band mark glyph, as in the
  // company list.
  isScore?: boolean
  sortValue?: SortValue<Row>
  render: (row: Row) => ReactNode
  cellClassName?: (row: Row) => string | false | undefined
  // Aligns the head with an inset its cells carry (the legend's chevron).
  headClassName?: string
}

export type ReadingColumn<Row> = ColumnBase<Row> & {
  role: ReadingColumnRole
  // The column hugs its content up to this cap, truncating past it.
  maxWidth?: string
  // Where the container cannot give the grow column its minimum, columns
  // shed lowest first; a column without one never sheds.
  shedOrder?: number
  // A list may be the table's one growing column (a report's Detections): it
  // sits last whatever the caller's order, takes the remaining width, shows
  // as many whole tokens as fit and "+N", and never wraps. Render it with
  // FittedTokenList.
  isGrowing?: boolean
}

// The long-text column (what happened, a definition): it grows, and always
// sits last. A table's one growing column is this or a growing list.
export type ReadingGrowColumn<Row> = ColumnBase<Row> & {
  // Clamp at two lines (the default) or show every line.
  lines?: 2 | "all"
}

const roleCellClassName: Record<ReadingColumnRole, string> = {
  identity: "",
  text: "",
  numeric: "tabular-nums",
  date: "type-count text-muted-foreground whitespace-nowrap",
  list: "",
}

const remToPx = (length: string) => {
  const rem = Number.parseFloat(length)
  const root =
    typeof document === "undefined"
      ? 16
      : Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
  return length.endsWith("rem") ? rem * root : rem
}

const horizontalPadding = (cell: HTMLElement) => {
  const style = getComputedStyle(cell)
  return (
    Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight)
  )
}

// The narrowest a capped column (a company name) gives back to.
const CAPPED_FLOOR_PX = 128

// The width a column needs: its measured content with padding, or, before it
// has rendered, its cap or a typical 8rem.
const neededWidth = <Row,>(
  column: ReadingColumn<Row>,
  naturalByKey: Map<string, number>
) => naturalByKey.get(column.key) ?? remToPx(column.maxWidth ?? "8rem")

// The narrowest a column goes before anything sheds: a capped column (a
// company name) down to its floor, every other column its measured width.
const floorWidth = <Row,>(
  column: ReadingColumn<Row>,
  naturalByKey: Map<string, number>,
  paddingByKey: Map<string, number>
) => {
  const needed = neededWidth(column, naturalByKey)
  if (!column.maxWidth) return needed
  return Math.min(needed, CAPPED_FLOOR_PX + (paddingByKey.get(column.key) ?? 0))
}

// The hugging columns a container of `width` keeps. Beside a grow column, the
// grow column's minimum wins over every hugging column, of any role; in a
// table of short facts, the columns must fit. Either way a capped column (a
// company name) gives back width first, down to an 8rem floor, truncating to
// its tooltip; while the row still does not fit, the lowest shedOrder goes.
const keptColumns = <Row,>(
  columns: ReadingColumn<Row>[],
  minimum: number,
  naturalByKey: Map<string, number>,
  paddingByKey: Map<string, number>,
  width: number | null
) => {
  if (width === null) return columns
  let kept = columns
  const fixedWidth = () =>
    kept.reduce(
      (sum, column) => sum + floorWidth(column, naturalByKey, paddingByKey),
      0
    )
  for (const column of shedOrdered(columns)) {
    if (width - fixedWidth() >= minimum) break
    kept = kept.filter((candidate) => candidate !== column)
  }
  return kept
}

const shedOrdered = <Row,>(columns: ReadingColumn<Row>[]) =>
  columns
    .filter((column) => column.shedOrder !== undefined)
    .toSorted((a, b) => (a.shedOrder ?? 0) - (b.shedOrder ?? 0))

export const ReadingTable = <Row,>({
  caption,
  columns,
  grow,
  rows,
  rowKey,
  onRowHover,
  isLive = Boolean(onRowHover),
  rowClassName,
  emptyState,
  isPinned = true,
  initialSort = null,
}: {
  caption: string
  columns: ReadingColumn<Row>[]
  // The one narrative column, if the table has one.
  grow?: ReadingGrowColumn<Row>
  rows: Row[]
  rowKey: (row: Row) => string
  // Reports the hovered row, so the dock swarm can flash its cell.
  onRowHover?: (row: Row | null) => void
  // A row that is a company takes the live-row edge and tint.
  isLive?: boolean
  rowClassName?: (row: Row) => string | false | undefined
  emptyState?: ReactNode
  isPinned?: boolean
  // The sort the table opens on; unsorted when absent.
  initialSort?: { key: string; isDescending: boolean } | null
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const [width, setWidth] = useState<number | null>(null)
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width)
    )
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  const sortValueByKey = useMemo(
    () =>
      Object.fromEntries(
        [...columns, ...(grow ? [grow] : [])].flatMap((column) =>
          column.sortValue ? [[column.key, column.sortValue]] : []
        )
      ) as Record<string, SortValue<Row>>,
    [columns, grow]
  )
  const { sort, sortedRows, toggleSort, ariaSort } = useTableSort(
    rows,
    sortValueByKey,
    initialSort
  )

  // Each column's natural width (its widest cell or head, with padding) is
  // measured once rendered and remembered by key, so a shed column can
  // return; it decides what sheds and how far a capped name gives back.
  // Each column's horizontal padding, measured with its natural width.
  const paddingByKey = useRef(new Map<string, number>()).current
  const [naturalByKey, setNaturalByKey] = useState(
    () => new Map<string, number>()
  )
  // The one growing column: the long-text column, or a growing list.
  const growingList = grow
    ? undefined
    : columns.find((column) => column.role === "list" && column.isGrowing)
  let minimum = 0
  // The width below which other columns shed: 12rem for the long-text
  // column; for a growing list its widest first token and the "+N" room, as
  // measured, so every row keeps at least one whole token.
  if (grow) minimum = remToPx("12rem")
  else if (growingList)
    minimum = naturalByKey.get(growingList.key) ?? remToPx("8rem")
  const kept = keptColumns(
    columns.filter((column) => column !== growingList),
    minimum,
    naturalByKey,
    paddingByKey,
    width
  )
  type OrderedColumn = ColumnBase<Row> & {
    role: ReadingColumnRole | "grow"
    maxWidth?: string
  }
  const takesRemainder = (column: OrderedColumn) =>
    column.role === "grow" || column.key === growingList?.key
  let ordered: OrderedColumn[] = kept
  if (grow) ordered = [...kept, { ...grow, role: "grow" }]
  // The growing column is always last, a chip list included, so the spare
  // width ends on the right; a caller's order cannot move it.
  else if (growingList) ordered = [...kept, growingList]
  const lastIndex = ordered.length - 1

  // Still short of room (the narrative column's minimum, or a table of facts'
  // own width) once everything sheddable has gone, the capped columns (a
  // company name) give back width, evenly, down to an 8rem floor, truncating
  // to their tooltip. Worked from the widths measured at full cap, so it
  // settles in one pass.
  const capByKey = (() => {
    const caps = new Map<string, number>()
    if (width === null) return caps
    const used = kept.reduce(
      (sum, column) => sum + neededWidth(column, naturalByKey),
      0
    )
    let deficit = minimum - (width - used)
    const capped = kept.filter(
      (column) => column.maxWidth && naturalByKey.has(column.key)
    )
    if (deficit <= 0 || capped.length === 0) return caps
    const floor = CAPPED_FLOOR_PX
    for (const column of capped) {
      const content = Math.min(
        remToPx(column.maxWidth ?? "0"),
        (naturalByKey.get(column.key) ?? 0) -
          (paddingByKey.get(column.key) ?? 0)
      )
      const take = Math.min(
        Math.max(0, content - floor),
        deficit / capped.length
      )
      if (take <= 0) continue
      caps.set(column.key, content - take)
      deficit -= take
    }
    return caps
  })()

  // Fonts that load after the first measure change every width.
  const [isFontsReady, setFontsReady] = useState(false)
  useEffect(() => {
    void document.fonts?.ready.then(() => setFontsReady(true))
  }, [])
  // Measured when what the cells hold or where they sit changes, not on every
  // render: a hover's re-render would otherwise force a layout read of every
  // cell.
  const measuredKey = [
    ...ordered.map(({ key }) => key),
    "|",
    ...capByKey.keys(),
  ].join(",")
  useLayoutEffect(() => {
    const table = tableRef.current
    if (!table) return
    const next = new Map(naturalByKey)
    let hasChanged = false
    const record = (key: string, natural: number) => {
      if (natural <= 0 || next.get(key) === natural) return
      next.set(key, natural)
      hasChanged = true
    }
    for (const column of ordered) {
      // A column compressed under its cap keeps its full-cap measure.
      if (column.key === growingList?.key) {
        let first = 0
        let padding = 0
        for (const cell of table.querySelectorAll<HTMLElement>(
          `td[data-column="${column.key}"]`
        )) {
          const token = cell.querySelector<HTMLElement>(
            "[data-fit-measure] > :first-child"
          )
          if (!token) continue
          padding = horizontalPadding(cell)
          first = Math.max(first, token.offsetWidth)
        }
        record(
          column.key,
          first > 0
            ? Math.ceil(first + TOKEN_GAP_PX + OVERFLOW_RESERVE_PX + padding)
            : 0
        )
        continue
      }
      if (takesRemainder(column) || capByKey.has(column.key)) continue
      // A column's padding is read once, at its first measure: the last
      // column carries the row's edge inset instead of a gap, so a column
      // that becomes last when its neighbour sheds would otherwise measure
      // narrower, un-shed the neighbour, and flip forever.
      let natural = 0
      let padding = paddingByKey.get(column.key) ?? 0
      for (const cell of table.querySelectorAll<HTMLElement>(
        `[data-column="${column.key}"]`
      )) {
        const content = cell.querySelector<HTMLElement>("[data-measure]")
        if (!content) continue
        if (padding === 0) padding = horizontalPadding(cell)
        natural = Math.max(
          natural,
          Math.ceil(content.getBoundingClientRect().width) + padding
        )
      }
      record(column.key, natural)
      if (padding > 0) paddingByKey.set(column.key, padding)
    }
    if (hasChanged) setNaturalByKey(next)
  }, [rows, columns, grow, width, measuredKey, isFontsReady])

  // Every head sits in one box, so every label shares one baseline. A
  // growing column's head never sets its width: it truncates to whatever the
  // column is given, so only the hugging columns hold the table open.
  const headFitting = (column: OrderedColumn, head: ReactNode) => (
    <div className={cn("flex", takesRemainder(column) && "w-0 min-w-full")}>
      {head}
    </div>
  )
  // Without a growing column, an empty spacer takes the remaining width, so
  // the columns pack left and the row skin still spans the well.
  const hasSpacer = !grow && !growingList
  // The grow column clamps at two lines unless it shows every line; a capped
  // identity column (a company name) holds its cap, so it measures the same
  // in every table and never shifts between sibling tables (Dhanur, 26 Oct
  // 2026); every other column hugs its content up to its cap.
  const cellContent = (column: OrderedColumn, row: Row) => {
    if (column.key === growingList?.key) return column.render(row)
    if (column.role !== "grow") {
      const capped = capByKey.get(column.key)
      const cap = capped === undefined ? column.maxWidth : `${capped}px`
      return (
        <div
          data-measure=""
          className="inline-block max-w-full min-w-0 align-top leading-5"
          style={{
            maxWidth: cap,
            width:
              column.role === "identity" && column.maxWidth ? cap : undefined,
          }}
        >
          {column.render(row)}
        </div>
      )
    }
    if (grow?.lines === "all") return column.render(row)
    return <div className="line-clamp-2">{column.render(row)}</div>
  }

  return (
    <div ref={containerRef} className="w-full min-w-0">
      <Table
        ref={tableRef}
        className="w-full table-auto"
        containerClassName="overflow-visible"
      >
        <caption className="sr-only">{caption}</caption>
        <colgroup>
          {/* The grow column or the spacer takes the whole remainder, so
              every other column settles at its content's width. */}
          {ordered.map((column) => (
            <col
              key={column.key}
              style={{
                width: takesRemainder(column) ? "100%" : undefined,
              }}
            />
          ))}
          {hasSpacer && <col style={{ width: "100%" }} />}
        </colgroup>
        <TableHeader
          data-pinned={isPinned || undefined}
          className={
            isPinned ? PINNED_TABLE_HEAD_CLASS : TABLE_HEAD_FRAME_CLASS
          }
        >
          <TableRow className="hover:bg-transparent">
            {ordered.map((column, index) => {
              const headClassName = cn(
                edgeClassName(index, lastIndex),
                column.headClassName
              )
              if (column.sortValue)
                return (
                  <TableHead
                    key={column.key}
                    scope="col"
                    aria-sort={ariaSort(column.key)}
                    data-column={column.key}
                    className={cn(HEAD_CLASS, headClassName)}
                  >
                    {headFitting(
                      column,
                      <SortHeader
                        label={column.label}
                        title={`Sort by ${column.label.toLowerCase()}`}
                        isScore={column.isScore}
                        isActive={sort?.key === column.key}
                        isDescending={sort?.isDescending ?? true}
                        onSort={() => toggleSort(column.key, column.isScore)}
                      />
                    )}
                  </TableHead>
                )
              return (
                <TableHead
                  key={column.key}
                  scope="col"
                  data-column={column.key}
                  className={cn(HEAD_CLASS, headClassName)}
                >
                  {column.isLabelHidden ? (
                    <span className="sr-only">{column.label}</span>
                  ) : (
                    headFitting(column, <StaticHeader label={column.label} />)
                  )}
                </TableHead>
              )
            })}
            {hasSpacer && (
              <TableHead aria-hidden="true" className={cn(HEAD_CLASS, "p-0")} />
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.length === 0 && emptyState ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={ordered.length + Number(hasSpacer)}
                className="py-3 pl-(--terminal-row-inset)"
              >
                {emptyState}
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((row) => (
              <TableRow
                key={rowKey(row)}
                onMouseEnter={onRowHover && (() => onRowHover(row))}
                onMouseLeave={onRowHover && (() => onRowHover(null))}
                className={cn(
                  "border-border-faint",
                  isLive ? "live-row" : "hover:bg-transparent",
                  rowClassName?.(row)
                )}
              >
                {ordered.map((column, index) => (
                  <TableCell
                    key={column.key}
                    data-column={column.key}
                    className={cn(
                      CELL_CLASS,
                      // Every cell reads from the row's top, and its first
                      // line sits in the row's 20px line box, so text of any
                      // size (a caption date beside a name) shares one
                      // baseline however the narrative column wraps.
                      "align-top",
                      edgeClassName(index, lastIndex),
                      column.role === "grow"
                        ? "whitespace-normal"
                        : cn(
                            "whitespace-nowrap",
                            roleCellClassName[column.role]
                          ),
                      column.cellClassName?.(row)
                    )}
                  >
                    {cellContent(column, row)}
                  </TableCell>
                ))}
                {hasSpacer && <TableCell aria-hidden="true" className="p-0" />}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}

// A list token: a governed word (a finding chip, an OSHA grade) in the neutral
// token, or a detection, labelled by its type in the coloured pill of its
// class, the same pill a detection card wears.
type ReadingToken = string | { label: string; detectionClass: DetectionClass }
const Token = ({ token }: { token: ReadingToken }) =>
  typeof token === "string" ? (
    <span className="terminal-table-token">{token}</span>
  ) : (
    <DetectionPill label={token.label} detectionClass={token.detectionClass} />
  )

// Governed tokens on one line, then "+N", the number not shown: a hugging list
// shows its first token; the table's growing list shows as many whole tokens
// as its width holds. Hovering "+N" shows every token, in the row's order, as
// the same pills, stacked in the shared tooltip, so it always holds the shown
// tokens plus N.
const MAX_VISIBLE_TOKENS = 1

export const Overflow = ({ count }: { count: number }) => (
  <span className="type-micro text-muted-foreground shrink-0">+{count}</span>
)

const withEveryToken = (
  tokens: ReadingToken[],
  shown: ReactElement<{ onFocus?: never; onMouseEnter?: never }>
) => (
  <TerminalOverflowTooltip
    label={undefined}
    content={
      <span className="flex flex-col items-start gap-1">
        {tokens.map((token, index) => (
          <Token key={index} token={token} />
        ))}
      </span>
    }
  >
    {shown}
  </TerminalOverflowTooltip>
)

export const ReadingTokenList = ({ tokens }: { tokens: ReadingToken[] }) => {
  if (tokens.length === 0) return <AbsenceDash />
  const overflow = tokens.length - MAX_VISIBLE_TOKENS
  const shown = (
    <span className="flex min-w-0 items-center gap-1 overflow-hidden">
      {tokens.slice(0, MAX_VISIBLE_TOKENS).map((token, index) => (
        <Token key={index} token={token} />
      ))}
      {overflow > 0 && <Overflow count={overflow} />}
    </span>
  )
  return overflow > 0 ? withEveryToken(tokens, shown) : shown
}

// The "+N" a fitted list reserves room for (wide enough for two digits).
const OVERFLOW_RESERVE_PX = 28
const TOKEN_GAP_PX = 4

// The growing list's cell: every token measured in an invisible copy, then
// as many whole tokens as the cell holds, the rest as "+N" (only "+N" when not
// even one fits). It never wraps and
// never widens its column: the tokens sit out of flow, so the column's width
// is the table's to give.
export const FittedTokenList = ({ tokens }: { tokens: ReadingToken[] }) => {
  const boxRef = useRef<HTMLSpanElement>(null)
  const measureRef = useRef<HTMLSpanElement>(null)
  const [count, setCount] = useState(tokens.length)
  useLayoutEffect(() => {
    const box = boxRef.current
    const measure = measureRef.current
    if (!box || !measure) return
    const fit = () => {
      const width = box.clientWidth
      const rights = [...measure.children].map(
        (child) =>
          (child as HTMLElement).offsetLeft + (child as HTMLElement).offsetWidth
      )
      let next = rights.length
      while (
        next > 0 &&
        rights[next - 1] +
          (next < rights.length ? TOKEN_GAP_PX + OVERFLOW_RESERVE_PX : 0) >
          width
      )
        next -= 1
      setCount(next)
    }
    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(box)
    return () => observer.disconnect()
  }, [tokens])
  if (tokens.length === 0) return <AbsenceDash />
  const overflow = tokens.length - count
  const shown = (
    <span className="absolute inset-0 flex items-center gap-1 overflow-hidden">
      {tokens.slice(0, count).map((token, index) => (
        <Token key={index} token={token} />
      ))}
      {overflow > 0 && <Overflow count={overflow} />}
    </span>
  )
  return (
    <span ref={boxRef} className="relative block h-5 w-full overflow-hidden">
      <span
        ref={measureRef}
        data-fit-measure=""
        aria-hidden="true"
        className="invisible absolute top-0 left-0 flex gap-1 whitespace-nowrap"
      >
        {tokens.map((token, index) => (
          <Token key={index} token={token} />
        ))}
      </span>
      {overflow > 0 ? withEveryToken(tokens, shown) : shown}
    </span>
  )
}
